import { api } from "@/api";
import { PricingPageConfig, ToolStatusConfig } from "@/store/configStore";

/**
 * Configuration key type definition
 */
export type ConfigKey = "tool_status_config" | "pricing_page_config";

/**
 * Type mapping for config keys to their respective data types
 */
export type ConfigTypeMap = {
  tool_status_config: ToolStatusConfig;
  pricing_page_config: PricingPageConfig;
};

/**
 * Type for the result of fetching multiple configs
 */
export type MultipleConfigsResult = Partial<ConfigTypeMap>;

/**
 * Generic config fetcher
 * Fetches configuration from the server
 */
export async function fetchConfig<K extends ConfigKey>(
  key: K,
): Promise<ConfigTypeMap[K] | null> {
  try {
    const res = await api.configs.getConfig({
      keys: [key],
    });

    const configItem = res.data.values?.find((v) => v.key === key);

    if (configItem?.value) {
      try {
        return JSON.parse(configItem.value) as ConfigTypeMap[K];
      } catch (e) {
        console.error(`Failed to parse ${key}`, e);
      }
    }
  } catch (error) {
    console.error(`Failed to fetch config: ${key}`, error);
  }

  return null;
}

/**
 * Fetch multiple configs in a single API call
 */
export async function fetchMultipleConfigs(
  keys: ConfigKey[],
): Promise<MultipleConfigsResult> {
  try {
    const res = await api.configs.getConfig({
      keys,
    });

    const configs: Partial<Record<ConfigKey, unknown>> = {};

    res.data.values?.forEach((item) => {
      if (item.key && item.value) {
        try {
          configs[item.key as ConfigKey] = JSON.parse(item.value);
        } catch (e) {
          console.error(`Failed to parse config: ${item.key}`, e);
        }
      }
    });

    return configs as MultipleConfigsResult;
  } catch (error) {
    console.error("Failed to fetch configs:", error);
    return {};
  }
}
